/**
 * Helpers for working with the runtime URL prefix the app is served from.
 *
 * The single source of truth at runtime is the <base href> element that
 * `index.html` injects synchronously before the bundle loads. Reading from
 * that tag (rather than `window.location.pathname`) keeps the value stable
 * regardless of which deep route the user is currently on.
 *
 * Examples:
 *   <base href="/">             -> getBasePath()    === ""
 *                                  getBasePathHref()=== "/"
 *   <base href="/123/">         -> getBasePath()    === "/123"
 *                                  getBasePathHref()=== "/123/"
 */

const readBaseHref = (): string => {
  if (typeof document === "undefined") {
    return "/";
  }
  return document.querySelector("base")?.getAttribute("href") || "/";
};

/**
 * The base href as written in the document, always trailing-slashed.
 * Suitable for building absolute URLs: `window.location.origin + getBasePathHref()`.
 */
export const getBasePathHref = (): string => {
  const href = readBaseHref();
  return href.endsWith("/") ? href : `${href}/`;
};

/**
 * The base path with no trailing slash, suitable as a path segment:
 *   "" for the root deployment, "/123" for a preview at /123/.
 */
export const getBasePath = (): string => {
  const href = getBasePathHref();
  if (href === "/") {
    return "";
  }
  return href.replace(/\/+$/, "");
};

/**
 * Absolute URL for the root of the current deployment, e.g.
 *   "https://preview.aiapp.datapay.co.nz/123/"
 *
 * Use this for Auth0 returnTo / fallback redirects so post-auth navigation
 * lands back on the correct path-prefixed app.
 */
export const getDeploymentRootUrl = (): string => {
  return window.location.origin + getBasePathHref();
};

/**
 * Auth0 `redirect_uri` for the current deployment.
 *
 * Path-prefixed previews share one whitelisted callback at `{origin}/callback`
 * (handled by the callback bouncer). Root deployments (local, sandbox,
 * production) use the app origin so Auth0 returns to `/`.
 */
export const getAuth0RedirectUri = (): string => {
  if (getBasePath()) {
    return `${window.location.origin}/callback`;
  }
  return window.location.origin;
};

/**
 * Auth0 logout `returnTo` for the current deployment.
 *
 * Path-prefixed previews use a fixed `{origin}/logout` URL (handled by the
 * logout bouncer). Auth0 ignores query strings when validating allow-listed
 * logout URLs, so the real destination is passed as `?returnTo=`. Root
 * deployments return directly to the deployment root.
 */
export const getAuth0LogoutReturnTo = (): string => {
  const destination = getDeploymentRootUrl();
  if (getBasePath()) {
    const bouncer = new URL(`${window.location.origin}/logout`);
    bouncer.searchParams.set("returnTo", destination);
    return bouncer.href;
  }
  return destination;
};
